import React, { useState, useRef } from 'react';
import { submitInquiry } from '../utils/api';
import { validateEmail, validateRequired, validateMessageQuality } from '../utils/validation';

interface ContactFormProps {
  className?: string;
  initialData?: {
    coffee?: string | null;
    subject?: string | null;
  };
}

interface FormData {
  name: string;
  email: string;
  company: string;
  subject: string;
  message: string;
}

export const ContactForm: React.FC<ContactFormProps> = ({ className = '', initialData }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    subject: initialData?.subject || '',
    message: initialData?.coffee ? `I'm interested in learning more about ${initialData.coffee}. Please provide additional information about availability, pricing, and samples.` : ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [honeypot, setHoneypot] = useState('');
  const formRenderedAt = useRef(Date.now());

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    if (!validateRequired(formData.name)) newErrors.name = 'Name is required';
    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!validateRequired(formData.subject)) newErrors.subject = 'Please select a subject';
    if (!validateRequired(formData.message)) {
      newErrors.message = 'Message is required';
    } else if (!validateMessageQuality(formData.message)) {
      newErrors.message = 'Please enter a few more details about your inquiry';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const result = await submitInquiry({
        name: formData.name,
        email: formData.email,
        company: formData.company,
        subject: formData.subject,
        message: formData.message,
        coffeeName: initialData?.coffee || '',
        website: honeypot,
        formRenderedAt: formRenderedAt.current,
      });

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          subject: initialData?.subject || '',
          message: initialData?.coffee ? `I'm interested in learning more about ${initialData.coffee}. Please provide additional information about availability, pricing, and samples.` : ''
        });
      } else {
        throw new Error(result.error || 'Failed to submit inquiry');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-3.5 py-2.5 bg-white border text-sm text-[#404040] placeholder-[#A3A3A3] transition-colors duration-200 focus:outline-none focus:border-[#E85C27] ${
      hasError ? 'border-red-400' : 'border-[#E8E6E2]'
    }`;

  return (
    <div className={`border border-[#E8E6E2] p-8 md:p-10 ${className}`}>
      <div className="mb-8">
        <h3 className="text-xl font-display text-[#404040] mb-2">Send us a message</h3>
        <p className="font-display text-sm text-[#6B6B6B]">Get in touch about partnerships, samples, or general inquiries.</p>
      </div>

      {submitStatus === 'success' && (
        <div className="mb-8 p-5 border border-green-200 bg-green-50">
          <p className="text-sm font-medium text-green-800 mb-1">Message sent successfully</p>
          <p className="text-sm text-[#6B6B6B]">We'll get back to you within 24 hours.</p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mb-8 p-5 border border-red-200 bg-red-50">
          <p className="text-sm font-medium text-red-800 mb-1">Submission failed</p>
          <p className="text-sm text-[#6B6B6B]">
            Please try again or email us directly at{' '}
            <a href="mailto:sidney@lot20.coffee" className="text-[#404040] underline">sidney@lot20.coffee</a>.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        {/* Honeypot — hidden from real users, bots that auto-fill every field will trip it */}
        <div aria-hidden="true" className="absolute left-[-9999px] top-auto w-px h-px overflow-hidden">
          <label htmlFor="website">Leave this field blank</label>
          <input
            type="text"
            id="website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="name" className="block text-xs uppercase tracking-wider text-[#A3A3A3] mb-1.5">
              Full Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Your full name"
              className={inputClass(!!errors.name)}
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-xs uppercase tracking-wider text-[#A3A3A3] mb-1.5">
              Email Address <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your@company.com"
              className={inputClass(!!errors.email)}
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="company" className="block text-xs uppercase tracking-wider text-[#A3A3A3] mb-1.5">
            Company / Roastery
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            placeholder="Your company or roastery name"
            className={inputClass(false)}
          />
        </div>

        <div>
          <label htmlFor="subject" className="block text-xs uppercase tracking-wider text-[#A3A3A3] mb-1.5">
            Subject <span className="text-red-400">*</span>
          </label>
          <select
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            className={inputClass(!!errors.subject)}
          >
            <option value="">Select inquiry type</option>
            <option value="Sample Request">Sample Request</option>
            <option value="Partnership Inquiry">Partnership Inquiry</option>
            <option value="Decaf Service">Decaf Service</option>
            <option value="Nursery/Seedlings">Nursery/Seedlings</option>
            <option value="General Inquiry">General Inquiry</option>
            <option value="Other">Other</option>
          </select>
          {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
        </div>

        <div>
          <label htmlFor="message" className="block text-xs uppercase tracking-wider text-[#A3A3A3] mb-1.5">
            Message <span className="text-red-400">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            rows={5}
            placeholder="Tell us about your inquiry..."
            className={inputClass(!!errors.message)}
          />
          {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3.5 bg-[#E85C27] text-white text-sm font-medium tracking-wide hover:bg-[#C0451A] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
  );
};
